import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { ChatProvider, useChat } from './context/ChatContext';
import { NotificationProvider } from './context/NotificationContext';
import { KiTodoProvider } from './context/KiTodoContext';
import { JarvisRootProvider } from './components/heykiki/JarvisAvatar';
import DevAutoLogin from './components/heykiki/DevAutoLogin';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import { CustomerTransitionProvider } from './context/CustomerTransitionContext';
import { refreshCache as refreshKundenCache } from './api/kundenApi';
import { getAllTermine } from './api/termineApi';
import apiClient from './api/apiClient';
import { cacheMultiple, setMetadata, STORES } from './utils/offlineDB';
import ErrorBoundary from './components/ErrorBoundary';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Onboarding from './pages/Onboarding';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Import Chat Widget
import FloatingChatButton from './components/Chat/FloatingChatButton';

// Import PWA Components
import InstallPrompt from './components/PWA/InstallPrompt';
import OnlineStatus from './components/PWA/OnlineStatus';
import MobileInstallBanner from './components/PWA/MobileInstallBanner';
import { getHostBranding } from './utils/hostBranding';

// Import Branding Loader
import BrandingLoader from './components/BrandingLoader';

// Import Mobile Components
import BottomNav from './components/Mobile/BottomNav';
import MobileMenu from './pages/MobileMenu';
import { useIsMobile } from './hooks/useIsMobile';
import { useEmbed } from './hooks/useEmbed';

import Dashboard from './pages/Dashboard';
import Kundenliste from './pages/Kundenliste';
import Kundendetail from './pages/Kundendetail';
import Einstellungen from './pages/Einstellungen';
import Kalender from './pages/Kalender';
import Plantafel from './pages/Plantafel';
import Geschaeftszeiten from './pages/Geschaeftszeiten';
import BlockierteZeiten from './pages/BlockierteZeiten';
import Rechnungen from './pages/Rechnungen';
import RechnungErstellen from './pages/RechnungErstellen';
import DauerauftragErstellen from './pages/DauerauftragErstellen';
import Kostenvoranschlaege from './pages/Kostenvoranschlaege';
import KvaErstellen from './pages/KvaErstellen';
import Katalog from './pages/Katalog';
import Textbausteine from './pages/Textbausteine';
import Mitarbeiter from './pages/Mitarbeiter';
import Abwesenheiten from './pages/Abwesenheiten';
import Notifications from './pages/Notifications';
import KvaBestaetigung from './pages/KvaBestaetigung';
import TerminBestaetigung from './pages/TerminBestaetigung';
import Abonnement from './pages/Abonnement';
import Projekte from './pages/Projekte';
import ProjektDetail from './pages/ProjektDetail';
import ProjektForm from './pages/ProjektForm';
import KiZentrale from './pages/KiZentrale';
import AnfragenUebersicht from './pages/AnfragenUebersicht';
import AnfragenIndex from './pages/AnfragenIndex';
import AnfrageDetail from './pages/AnfrageDetail';
import PublicProjekt from './pages/PublicProjekt';

// Import Notification Components
import PushPermissionPrompt from './components/Notifications/PushPermissionPrompt';

// Import Super-Admin Components
import { SuperAdminProvider } from './context/SuperAdminContext';
import SuperAdminLogin from './pages/SuperAdmin/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import ImpersonateBanner from './components/SuperAdmin/ImpersonateBanner';
import EmbedAuthHandler from './components/Embed/EmbedAuthHandler';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Speichere aktuelle URL inkl. Query-Parameter für Redirect nach Login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // SUSPENDED-User dürfen nur die Abonnement-Seite sehen
  if (user.organisation?.planStatus === 'SUSPENDED' && !location.pathname.includes('/abonnement')) {
    return <Navigate to="/einstellungen/abonnement" replace />;
  }

  return children;
}

// Chat Widget - nur für eingeloggte Benutzer mit Berechtigung anzeigen
function AuthenticatedChatWidget() {
  const { user, loading } = useAuth();
  const { canView, loading: permissionsLoading } = usePermissions();
  const location = useLocation();
  const { isEmbed } = useEmbed();

  // Nicht anzeigen im Embed-Mode (HeyKiki iFrame oder Cockpit-Preview Demo).
  // useEmbed erkennt ?embed=1 UND iframe-Kontext — das ist wichtig, weil
  // KiZentrale.handleTabChange die Search-Params beim Tab-Wechsel komplett
  // überschreibt und embed=1 dabei verloren gehen würde.
  if (isEmbed) {
    return null;
  }

  // Nicht anzeigen auf Super-Admin-Seiten
  if (location.pathname.startsWith('/super-admin')) {
    return null;
  }

  // Nicht anzeigen wenn nicht eingeloggt oder noch lädt
  if (loading || permissionsLoading || !user) {
    return null;
  }

  // Nicht anzeigen wenn keine Berechtigung für KI-Assistent
  if (!canView('KI_ASSISTENT')) {
    return null;
  }

  return <FloatingChatButton />;
}

// Bottom Navigation - nur für eingeloggte Benutzer anzeigen, versteckt wenn Chat offen auf Mobile
function AuthenticatedBottomNav() {
  const { user, loading } = useAuth();
  const { isChatOpen } = useChat();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { isEmbed } = useEmbed();

  // Nicht anzeigen im Embed-Mode (HeyKiki iFrame oder Cockpit-Preview Demo)
  if (isEmbed) {
    return null;
  }

  // Nicht anzeigen auf Login-Seite, wenn nicht eingeloggt oder noch lädt
  if (loading || !user || location.pathname === '/login') {
    return null;
  }

  // Auf Mobile ausblenden wenn Chat offen ist
  if (isMobile && isChatOpen) {
    return null;
  }

  return <BottomNav />;
}

// Push Permission Prompt - nur für eingeloggte Benutzer auf Mobile anzeigen, nicht auf Desktop
function AuthenticatedPushPrompt() {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Nicht anzeigen auf Login-Seite, wenn nicht eingeloggt oder noch lädt
  if (loading || !user || location.pathname === '/login') {
    return null;
  }

  // Auf Desktop keine Push-Prompts anzeigen
  if (!isMobile) {
    return null;
  }

  return <PushPermissionPrompt />;
}

// Initial Cache Loader - lädt wichtige Daten beim App-Start in den Cache
function InitialCacheLoader() {
  const { user, loading } = useAuth();

  useEffect(() => {
    const loadInitialCache = async () => {
      // Nur wenn eingeloggt und online
      if (!user || !navigator.onLine) return;

      try {
        console.log('📦 Initialer Cache wird geladen...');

        // Kunden in Cache laden
        await refreshKundenCache();

        // Termine in Cache laden (für Kalender offline)
        try {
          // Lade Termine für die nächsten 3 Monate
          await getAllTermine();
          console.log('📦 Termine gecached');
        } catch (termineError) {
          console.error('Termine-Cache-Fehler:', termineError);
        }

        // Katalogpositionen in Cache laden (für Zeiterfassung offline)
        try {
          const katalogResponse = await apiClient.get('/katalog');
          const katalog = katalogResponse.data || [];
          if (katalog.length > 0) {
            await cacheMultiple(STORES.CACHE_KATALOG, katalog);
            await setMetadata(`${STORES.CACHE_KATALOG}-last-sync`, Date.now());
            console.log(`📦 ${katalog.length} Katalogpositionen gecached`);
          }
        } catch (katalogError) {
          console.error('Katalog-Cache-Fehler:', katalogError);
        }

        console.log('✅ Initialer Cache geladen');
      } catch (error) {
        console.error('Cache-Fehler:', error);
        // Fehler beim Cachen ist nicht kritisch, App funktioniert trotzdem
      }
    };

    if (!loading && user) {
      loadInitialCache();
    }
  }, [user, loading]);

  return null; // Keine UI, nur Side-Effect
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <PermissionProvider>
          <NotificationProvider>
            <KiTodoProvider>
            <ChatProvider>
              <CustomerTransitionProvider>
              {/* MyKiki (ElevenLabs Conversational AI) — Persistent ueber Routen-Wechsel.
                  Side-effects sind null solange niemand startSession() ruft, daher
                  unschaedlich fuer Standalone-Orgs. */}
              <JarvisRootProvider>
              {/* Dev-only Auto-Login: greift NUR in vite dev mode + localhost +
                  wenn VITE_DEV_SSO_SECRET in .env.local hinterlegt ist. In jedem
                  anderen Setup (production-build, anderer Host) silent. */}
              <DevAutoLogin />
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#1a1a1a',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                },
                success: {
                  iconTheme: {
                    primary: '#057867',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <Routes>
          {/* Super-Admin Routes (separate Auth) */}
          <Route path="/super-admin/login" element={
            <SuperAdminProvider>
              <SuperAdminLogin />
            </SuperAdminProvider>
          } />
          <Route path="/super-admin/dashboard" element={
            <SuperAdminProvider>
              <SuperAdminDashboard />
            </SuperAdminProvider>
          } />

          {/* Embed Auth (HeyKiki iFrame SSO) */}
          <Route path="/embed" element={<EmbedAuthHandler />} />

          {/* Public Routes */}
          <Route path="/public/projekt/:token" element={<PublicProjekt />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/anfragen"
            element={
              <ProtectedRoute>
                <AnfragenIndex />
              </ProtectedRoute>
            }
          />

          <Route
            path="/anfragen/liste"
            element={
              <ProtectedRoute>
                <AnfragenUebersicht />
              </ProtectedRoute>
            }
          />

          <Route
            path="/anfragen/:id"
            element={
              <ProtectedRoute>
                <AnfrageDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kunden"
            element={
              <ProtectedRoute>
                <Kundenliste />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kunden/:id"
            element={
              <ProtectedRoute>
                <Kundendetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ki-zentrale"
            element={
              <ProtectedRoute>
                <KiZentrale />
              </ProtectedRoute>
            }
          />

          <Route
            path="/einstellungen"
            element={
              <ProtectedRoute>
                <Einstellungen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/einstellungen/abonnement"
            element={
              <ProtectedRoute>
                <Abonnement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/einstellungen/telefonassistent"
            element={
              <ProtectedRoute>
                <Navigate to="/ki-zentrale?tab=telefon" replace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kalender"
            element={
              <ProtectedRoute>
                <Kalender />
              </ProtectedRoute>
            }
          />

          <Route
            path="/plantafel"
            element={
              <ProtectedRoute>
                <Plantafel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kalender/geschaeftszeiten"
            element={
              <ProtectedRoute>
                <Geschaeftszeiten />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kalender/blockierte-zeiten"
            element={
              <ProtectedRoute>
                <BlockierteZeiten />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projekte"
            element={
              <ProtectedRoute>
                <Projekte />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projekte/neu"
            element={
              <ProtectedRoute>
                <ProjektForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projekte/:id"
            element={
              <ProtectedRoute>
                <ProjektDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projekte/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <ProjektForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rechnungen"
            element={
              <ProtectedRoute>
                <Rechnungen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rechnungen/neu"
            element={
              <ProtectedRoute>
                <RechnungErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rechnungen/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <RechnungErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rechnungen/dauerauftrag/neu"
            element={
              <ProtectedRoute>
                <DauerauftragErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rechnungen/dauerauftrag/:id"
            element={
              <ProtectedRoute>
                <DauerauftragErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kostenvoranschlaege"
            element={
              <ProtectedRoute>
                <Kostenvoranschlaege />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kostenvoranschlaege/neu"
            element={
              <ProtectedRoute>
                <KvaErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/kostenvoranschlaege/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <KvaErstellen />
              </ProtectedRoute>
            }
          />

          <Route
            path="/katalog"
            element={
              <ProtectedRoute>
                <Katalog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/textbausteine"
            element={<Navigate to="/katalog?tab=textbausteine" replace />}
          />

          <Route
            path="/mitarbeiter"
            element={
              <ProtectedRoute>
                <Mitarbeiter />
              </ProtectedRoute>
            }
          />

          <Route
            path="/abwesenheiten"
            element={
              <ProtectedRoute>
                <Abwesenheiten />
              </ProtectedRoute>
            }
          />

          <Route
            path="/menu"
            element={
              <ProtectedRoute>
                <MobileMenu />
              </ProtectedRoute>
            }
          />

          <Route
            path="/benachrichtigungen"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* /kva-bestaetigung war eine tote Zwischenseite — jetzt Redirect auf die
              Kostenvoranschläge-Liste (Feedback 2026-04-23). Route bleibt bestehen,
              damit alte Notification-Links, Bookmarks und E-Mails nicht ins Leere laufen. */}
          <Route path="/kva-bestaetigung" element={<Navigate to="/kostenvoranschlaege" replace />} />
          <Route path="/kva-bestaetigung/:id" element={<Navigate to="/kostenvoranschlaege" replace />} />

          <Route
            path="/termin-bestaetigung"
            element={
              <ProtectedRoute>
                <TerminBestaetigung />
              </ProtectedRoute>
            }
          />

          <Route
            path="/termin-bestaetigung/:id"
            element={
              <ProtectedRoute>
                <TerminBestaetigung />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

          {/* Super-Admin Impersonate Banner */}
          <ImpersonateBanner />

          {/* Mobile Bottom Navigation */}
          <AuthenticatedBottomNav />

          {/* Floating Chat Widget - nur für eingeloggte User */}
          <AuthenticatedChatWidget />

          {/* Initial Cache Loader */}
          <InitialCacheLoader />

          {/* PWA Components — auf HeyKiki-Portalen ausgeblendet */}
          {!getHostBranding() && <>
            <MobileInstallBanner />
            <InstallPrompt />
          </>}
          <OnlineStatus />

          {/* Push Notification Permission Prompt - nur für eingeloggte User */}
          <AuthenticatedPushPrompt />

          {/* Branding Loader - lädt Design-Einstellungen beim App-Start */}
          <BrandingLoader />
              </JarvisRootProvider>
              </CustomerTransitionProvider>
            </ChatProvider>
            </KiTodoProvider>
          </NotificationProvider>
        </PermissionProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
